import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Platform,
  Linking,
  ScrollView,
} from 'react-native';

interface DeleteAccountConfirmModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** Optional URL for account deletion page (e.g. web flow). Shown as "You can also request deletion from our website." */
  accountDeletionUrl?: string;
}

/**
 * Confirmation modal for permanent account deletion.
 * Satisfies App Store Guideline 5.1.1(v) - in-app option to initiate account deletion.
 */
const DeleteAccountConfirmModal: React.FC<DeleteAccountConfirmModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  accountDeletionUrl,
}) => {
  const openDeletionUrl = () => {
    if (accountDeletionUrl) {
      Linking.openURL(accountDeletionUrl).catch(() => {});
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        <TouchableOpacity
          style={StyleSheet.absoluteFill}
          activeOpacity={1}
          onPress={onCancel}
        />
        <View style={styles.box} onStartShouldSetResponder={() => true}>
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            <Text style={styles.title}>Delete account?</Text>
            <Text style={styles.message}>
              Your account and all associated data will be permanently deleted. This cannot be undone.
            </Text>
            <Text style={styles.message}>
              You will lose access to your profile, appointments, and any saved data.
            </Text>
            {accountDeletionUrl ? (
              <Text style={styles.linkHint}>
                You can also request account deletion from our{' '}
                <Text style={styles.link} onPress={openDeletionUrl}>
                  website
                </Text>
                .
              </Text>
            ) : null}
            <View style={styles.buttons}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={onCancel}
                activeOpacity={0.7}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.confirmButton}
                onPress={onConfirm}
                activeOpacity={0.7}
              >
                <Text style={styles.confirmButtonText}>Delete my account</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  box: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 24,
    width: '100%',
    maxWidth: 320,
    maxHeight: '80%',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    }),
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.15,
      shadowRadius: 20,
      elevation: 8,
    }),
  },
  scroll: {
    maxHeight: 400,
  },
  scrollContent: {
    paddingBottom: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 8,
  },
  message: {
    fontSize: 15,
    color: '#555',
    lineHeight: 22,
    marginBottom: 12,
  },
  linkHint: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
    marginBottom: 20,
  },
  link: {
    color: '#007AFF',
    textDecorationLine: 'underline',
    fontWeight: '500',
  },
  buttons: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  cancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: '#f0f0f0',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
  },
  confirmButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: '#dc3545',
  },
  confirmButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
});

export default DeleteAccountConfirmModal;
